import { format } from 'date-fns';
import {
  addTrackingToExternalBookingUrl,
  getLessonJoinPageUrl,
  getLessonPageUrl,
} from 'constants/pages';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import AlertIcon from 'svg/AlertIcon';
import CoachBadge from 'svg/CoachBadge';
import Directions from 'svg/Directions';
import Location from 'svg/Location';
import { AvailableClinicCardProps } from 'screens/PlayerLoggedInScreen/props';
import { ButtonLink } from 'components/Button';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';

const AvailableClinicCard: React.FC<AvailableClinicCardProps> = ({ clinic, className }) => {
  const startDateTime = clinic?.startDateTime;
  const lessonDate = startDateTime && format(new Date(startDateTime), 'eee MMM. d');
  const lessonStartTime = startDateTime && format(new Date(startDateTime), 'p');
  const lessonEndTime = clinic?.endDateTime && format(new Date(clinic.endDateTime), 'p');
  const participantLimit = clinic?.participantLimit || 0;
  const participantCount = clinic?.participants.length;

  const spotsLeft = participantLimit - participantCount;

  const price = clinic.priceUnitAmount
    ? convertUnitPriceToFormattedPrice(clinic.priceUnitAmount, clinic.currency).priceDisplay
    : 0;

  const renderLocation = (
    <div className="mt-ds-md flex items-center gap-4 max-sm:mt-ds-xs">
      <div className="inline-flex gap-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
        <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile max-sm:line-clamp-1">
          {clinic?.userCustomCourt?.fullAddress || clinic?.venue?.addressString}
        </span>
      </div>
      <a
        target="_blank"
        href={`https://maps.google.com?q=${
          clinic?.userCustomCourt?.fullAddress || clinic?.venue?.addressString
        }`}
        className="inline-flex items-center gap-1 max-sm:hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Directions className="mb-[0.9px] h-[0.87rem] w-[0.87rem] shrink-0" />
        <span className="typography-product-button-label-small">Directions</span>
      </a>
    </div>
  );

  const renderParticipants = (
    <div className="isolate flex">
      {[...clinic?.participants].map((participant, index) => (
        <Participant
          index={index}
          key={participant.id}
          imagePath={getProfileImageUrlOrPlaceholder({
            path: participant.userProfile?.profileImagePath || '',
          })}
        />
      ))}
    </div>
  );

  return (
    <Link
      href={getLessonPageUrl(clinic.id)}
      className={classNames(
        'flex min-w-0 cursor-pointer gap-4 transition-all duration-200',
        className,
      )}
    >
      <img
        src={clinic.ownerProfile?.profileImageUrl || ''}
        alt="clinic-owner"
        className="max-h-[12.5rem] w-full max-w-[12.5rem] rounded-xl border border-color-border-input-lightmode bg-gray-100 object-cover object-center dark:border-color-border-input-darkmode max-sm:max-h-[7.5rem] max-sm:max-w-[7.5rem]"
      />

      <div className="flex   flex-1 flex-col ">
        <div className="flex justify-between gap-2">
          <div className="flex items-start gap-2">
            <span className="max-sm:typography-product-chips-filters typography-product-element-label text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {lessonDate} <span className="text-color-brand-primary">&bull;</span>{' '}
              {lessonStartTime} - {lessonEndTime}
            </span>
          </div>
          {!!price && <span className="typography-product-subheading">{price}</span>}
        </div>
        <span className="typography-product-card-title-desktop max-sm:typography-product-card-title-mobile mt-ds-xs sm:mt-ds-sm">
          {clinic.title}
        </span>
        <span className="typography-product-text-card-desktop mt-ds-sm line-clamp-3 max-sm:hidden">
          {clinic.description}
        </span>
        {renderLocation}
        <span className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile mt-ds-sm flex items-center gap-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary max-sm:mt-ds-xs">
          <CoachBadge className="h-4 w-4 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
          {clinic.ownerProfile?.fullName}
        </span>
        <div className="typography-product-text-card-desktop max-sm:typography-product-text-card-mobile mt-auto flex min-w-0 items-center justify-between  text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {!clinic.externalBookingUrl ? (
            <div className="flex items-center gap-ds-sm">
              <span>Players Going</span>
              <span>
                {participantCount}/{participantLimit}
              </span>
              {renderParticipants}
            </div>
          ) : (
            <div>&nbsp;</div>
          )}
          <div className="flex  min-w-fit items-center gap-ds-sm">
            {!clinic.externalBookingUrl ? (
              <span
                className={classNames(
                  'flex shrink-0 gap-1',
                  spotsLeft === 0 && 'text-color-brand-primary',
                )}
              >
                {spotsLeft === 0 && <AlertIcon className="h-4 w-4" viewBox="0 0 23 21" />}
                {spotsLeft} spots left
              </span>
            ) : (
              <div>&nbsp;</div>
            )}
            {!!clinic.externalBookingUrl?.trim() && (
              <ButtonLink
                variant="brand"
                size="sm"
                className="w-[6.25rem] max-sm:hidden"
                isExternal
                href={addTrackingToExternalBookingUrl(clinic.externalBookingUrl, clinic.id)}
                onClick={(e) => e.stopPropagation()}
              >
                Join
              </ButtonLink>
            )}
            {spotsLeft > 0 && !clinic.externalBookingUrl && (
              <ButtonLink
                variant="brand"
                size="sm"
                className="w-[6.25rem] max-sm:hidden"
                href={getLessonJoinPageUrl(clinic.id)}
                onClick={(e) => e.stopPropagation()}
              >
                Join
              </ButtonLink>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default AvailableClinicCard;

const Participant: React.FC<{ imagePath: string; index: number }> = ({ imagePath, index }) => (
  <div style={{ translate: index > 0 ? -(8 * index) : 0 }}>
    <img src={imagePath} alt="avatar" className="relative h-6 w-6 rounded-full" />
  </div>
);
